import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { namesList, plural } from "../../../../features/analytics/profileFindingsModel";
import type { GbpCategoryOption, GbpEditValue, GbpPhotoCategory, GbpServiceEdit } from "../../../../services/gbpService";
import { GBP_PHOTO_CATEGORIES } from "../../../../services/gbpConstants";
import type { MediaItem } from "../../../../types/MediaItem";
import { getMediaThumbnailUrl } from "../../../../types/MediaItem";
import type { ApplyItem, FixValue, GbpPhotoPage, GbpProposalAttachment } from "../utils/gbpProposalText";
import { ASK_GAPS, HANDOFF_GAPS, canRetry, isSettled, isWaiting, liveProgress } from "../utils/gbpProposalText";
import type { GbpPlan, GbpPlanRow } from "../utils/gbpPlan";
import {
  applyItemFor,
  approveAllItems,
  inApproveAll,
  isClosed,
  openFixes,
  outcomeOf,
  outcomeReport,
  rowValue,
  rowsIn,
  settingsRecheckSince,
  shown,
  sourceProblems,
} from "../utils/gbpPlan";
import { CategoryEditor, DateEditor, Dot, HoursEditor, OutcomeLine, WarningLine, statusWord } from "./GbpFixProposal";
import { GbpLoadingCard } from "./GbpLoadingCard";
import { MediaLibraryModal } from "./MediaLibraryModal";

/** "Oct 7": when the owner told us, in their locale. */
const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

/**
 * The Visibility Manager's Google Business Profile proposal, v2 (SGL-421).
 *
 * Every fix arrives prepared: written from the Brand, its knowledge files and
 * its search data, each with its reason and the evidence behind it. The owner
 * approves each row, skips it, edits it, or approves all. Optional details sit
 * collapsed at the end and are never counted as fixes.
 *
 * Presentational, like the v1 card: changes go up through onChange, approvals
 * through onApply (one edit per row, logged in GbpEditLog by social-management),
 * and Google's answers come back on `proposal.applied`.
 */

const STATUS_FIRST_MS = 5_000;
const STATUS_POLL_MS = 30_000;
const STATUS_POLL_MAX = 40;
const DESCRIPTION_MAX = 750;
const SERVICE_LINE_MAX = 300;

const BTN_PRIMARY =
  "h-9 rounded-[10px] bg-theme-primary px-3.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-theme-primary-hover disabled:cursor-not-allowed disabled:opacity-45";
const BTN_SECONDARY =
  "h-9 rounded-[10px] border border-theme-border bg-theme-surface px-3.5 text-[13.5px] font-semibold text-theme-fg transition-colors hover:border-theme-fg-muted disabled:cursor-not-allowed disabled:opacity-45";
const BTN_QUIET =
  "h-9 rounded-[10px] px-2.5 text-[13.5px] font-medium text-theme-fg-muted transition-colors hover:text-theme-fg disabled:opacity-45";
const INPUT =
  "w-full rounded-[10px] border border-theme-border bg-theme-surface px-3 py-2.5 text-[14px] text-theme-fg placeholder:text-theme-fg-muted focus:border-theme-fg-muted focus:outline-none focus:ring-0";
const SELECT =
  "w-auto min-w-[150px] rounded-[10px] border border-theme-border bg-theme-surface py-1.5 pl-3 pr-9 text-[13.5px] leading-6 text-theme-fg focus:border-theme-fg-muted focus:outline-none focus:ring-0";
const CARD =
  "rounded-[14px] border border-theme-border bg-theme-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-18px_rgba(16,24,40,0.22)]";
const KICKER = "text-[12.5px] tabular-nums text-theme-fg-muted";
const TITLE = "text-[19px] font-semibold leading-snug tracking-[-0.015em] text-theme-fg [text-wrap:balance]";
const MUTED = "text-[13.5px] leading-relaxed text-theme-fg-muted";

/** Where on Google the owner makes a change Sagelyn can't write. */
const WHERE_ON_GOOGLE: Record<string, string> = {
  service_area: "On Google: Edit profile → Location → Service area",
  name: "On Google: Edit profile → About → Business name",
};

const HANDOFF_SPEC: Record<string, keyof typeof HANDOFF_GAPS> = {
  posts: "no_posts",
  reviews: "reviews_without_reply",
};

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

/** The owner's question about a row, as it is sent to the agent. Names the row the
 *  way the card shows it, so the agent finds it in the card it reads. */
/** "Not sure how? Ask me" on the Google settings box: the agent guides them. */
export const GBP_SETTINGS_QUESTION =
  "How do I turn off Google's automatic updates on my Business Profile, and why does it matter?";

export const askAbout = (row: GbpPlanRow): string => {
  const what = shown(row.display) || shown(row.value);
  const name = what && what.length <= 80 ? `${row.label}: ${what}` : row.label;
  return `About “${name}” on my Business Profile card: what does it mean, and why do you suggest it?`;
};

export interface GbpPlanCardProps {
  proposal: GbpProposalAttachment;
  agentName?: string;
  onChange?: (next: GbpProposalAttachment) => void;
  onApply?: (items: ApplyItem[]) => void;
  onHandOff?: (agentId: string, prompt: string) => void;
  searchCategories?: (query: string) => Promise<GbpCategoryOption[]>;
  loadMediaLibrary?: (nextToken?: string | null) => Promise<GbpPhotoPage>;
  onConnect?: () => void;
  checkStatus?: (all?: boolean) => Promise<boolean>;
  /** Ask the Visibility Manager about a row; the question is sent as the owner's message. */
  onAsk?: (text: string) => void;
}

/* ── small pieces ──────────────────────────────────────────────────────── */

/** The row's state in one word, next to its title. */
function rowState(row: GbpPlanRow, card: GbpProposalAttachment): ["live" | "review" | "failed" | "none", string] {
  const outcome = outcomeOf(row, card);
  if (outcome) return statusWord(outcome);
  if (card.skipped?.[row.id]) return ["none", "Skipped"];
  if (card.doneOnGoogle?.[row.id]) return ["live", "Done on Google"];
  if (row.kind === "handoff") return ["none", "For your team"];
  if (row.kind === "manual") return ["none", "You change this on Google"];
  if (applyItemFor(row, card)) return ["none", inApproveAll(row, card) ? "Ready" : "Check first"];
  return ["none", row.status === "not_drafted" ? "Needs your words" : "Needs your answer"];
}

/** Now → new, in the shape the row's field has. */
const Change: React.FC<{ row: GbpPlanRow; card: GbpProposalAttachment }> = ({ row, card }) => {
  const value = rowValue(row, card);
  const before = shown(row.before);

  if (row.field === "services") {
    const list = (Array.isArray(value) ? value : []) as Array<string | GbpServiceEdit>;
    const items = list.map((x) => (typeof x === "string" ? { name: x, description: "" } : x));
    return (
      <ul className="grid gap-2">
        {items.map((s) => (
          <li key={s.name} className="rounded-[10px] bg-theme-background px-3.5 py-2.5">
            <p className="text-[14px] font-semibold text-theme-fg">{s.name}</p>
            {s.description ? (
              <p className="mt-0.5 text-[13.5px] leading-relaxed text-theme-fg">{s.description}</p>
            ) : (
              <p className="mt-0.5 text-[13px] text-theme-fg-muted">No description yet. Add one with Edit, or add the service as it is.</p>
            )}
          </li>
        ))}
        {row.id === "service_descriptions" && row.meta?.not_drafted && !card.planAnswers?.[row.id] && (
          <li className="text-[13px] text-theme-fg-muted">{row.meta.not_drafted}. Add it with Edit.</li>
        )}
      </ul>
    );
  }

  if (row.field === "description") {
    return (
      <div className="grid gap-2.5">
        {before && (
          <div>
            <p className={KICKER}>Now on Google</p>
            <p className="mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-theme-fg-muted line-through decoration-theme-border">
              {before}
            </p>
          </div>
        )}
        <div>
          <p className={KICKER}>{before ? "Rewritten" : "New"}</p>
          {typeof value === "string" && value ? (
            <p className="mt-1 whitespace-pre-line text-[14px] leading-relaxed text-theme-fg">{value}</p>
          ) : (
            <p className="mt-1 text-[13.5px] text-theme-fg-muted">
              I couldn't write this one{row.meta?.not_drafted ? ` (${row.meta.not_drafted})` : ""}. Write it with Edit.
            </p>
          )}
          {typeof value === "string" && value && (
            <p className="mt-1 text-[12px] tabular-nums text-theme-fg-muted">{value.length} / {DESCRIPTION_MAX}</p>
          )}
        </div>
      </div>
    );
  }

  const after =
    row.field === "primary_category" && row.kind === "ask"
      ? card.primaryAnswer?.display_name ?? ""
      : row.field === "attributes" || row.field === "regular_hours" || row.field === "opening_date"
        ? shown(card.planAnswers?.[row.id] ?? null)
        : card.planAnswers?.[row.id] != null
          ? shown(card.planAnswers[row.id])
          : shown(row.display ?? value);
  if (!before && !after) return null;
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[14px] text-theme-fg">
      {before && <span className="text-theme-fg-muted line-through decoration-theme-border">{before}</span>}
      {before && after && <span aria-hidden="true" className="text-theme-fg-muted">→</span>}
      {after && <span className="font-medium">{after}</span>}
    </p>
  );
};

/* ── editing a row ─────────────────────────────────────────────────────── */

const ServiceLinesEditor: React.FC<{
  value: GbpServiceEdit[];
  onSave: (v: GbpServiceEdit[]) => void;
  onCancel: () => void;
}> = ({ value, onSave, onCancel }) => {
  const [lines, setLines] = useState(() => value.map((s) => ({ ...s })));
  const tooLong = lines.some((s) => s.description.length > SERVICE_LINE_MAX);
  return (
    <div className="grid gap-3">
      {lines.map((s, k) => (
        <label key={s.name} className="grid gap-1 text-[13.5px] font-semibold text-theme-fg">
          {s.name}
          <textarea
            rows={2}
            value={s.description}
            maxLength={SERVICE_LINE_MAX + 50}
            onChange={(e) => setLines((all) => all.map((x, i) => (i === k ? { ...x, description: e.target.value } : x)))}
            placeholder="What this service includes"
            className={`${INPUT} font-normal`}
          />
          <span className="text-[12px] font-normal tabular-nums text-theme-fg-muted">
            {s.description.length} / {SERVICE_LINE_MAX}
          </span>
        </label>
      ))}
      <div className="flex gap-2">
        <button type="button" disabled={tooLong} onClick={() => onSave(lines.map((s) => ({ ...s, description: s.description.trim() })))} className={BTN_PRIMARY}>
          Save
        </button>
        <button type="button" onClick={onCancel} className={BTN_QUIET}>Cancel</button>
      </div>
    </div>
  );
};

const TextValueEditor: React.FC<{
  value: string;
  multiline?: boolean;
  max: number;
  label: string;
  onSave: (v: string) => void;
  onCancel: () => void;
}> = ({ value, multiline, max, label, onSave, onCancel }) => {
  const [draft, setDraft] = useState(value);
  const clean = draft.trim();
  const props = {
    value: draft,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    "aria-label": label,
    className: INPUT,
  };
  return (
    <div className="grid gap-2">
      {multiline ? <textarea rows={7} {...props} /> : <input type="text" {...props} />}
      {multiline && <span className="text-[12px] tabular-nums text-theme-fg-muted">{draft.length} / {max}</span>}
      <div className="flex gap-2">
        <button type="button" disabled={!clean || clean.length > max} onClick={() => onSave(clean)} className={BTN_PRIMARY}>
          Save
        </button>
        <button type="button" onClick={onCancel} className={BTN_QUIET}>Cancel</button>
      </div>
    </div>
  );
};

/* ── the card ──────────────────────────────────────────────────────────── */

interface LibraryState {
  open: boolean;
  loading: boolean;
  loadingMore: boolean;
  items: MediaItem[];
  nextToken: string | null;
  failed: boolean;
  moreFailed: boolean;
}

const LIBRARY_CLOSED: LibraryState = {
  open: false, loading: false, loadingMore: false, items: [], nextToken: null, failed: false, moreFailed: false,
};

export const GbpPlanCard: React.FC<GbpPlanCardProps> = ({
  proposal,
  agentName,
  onChange,
  onApply,
  onHandOff,
  searchCategories,
  loadMediaLibrary,
  onConnect,
  checkStatus,
  onAsk,
}) => {
  // The card's state lives on the message (stored, reloads); this copy keeps it
  // usable when nobody listens.
  const [card, setCard] = useState(proposal);
  useEffect(() => setCard(proposal), [proposal]);
  const update = (next: GbpProposalAttachment) => {
    setCard(next);
    onChange?.(next);
  };

  const [editing, setEditing] = useState<string | null>(null);
  // The change on screen: the card goes through them one at a time, so the
  // owner can ask about each. null until they move: the first one still open.
  const [step, setStep] = useState<number | null>(null);
  const [library, setLibrary] = useState<LibraryState>(LIBRARY_CLOSED);
  const librarySeq = useRef(0);

  const plan = card.plan as GbpPlan | null | undefined;
  const outcomes = Object.values(card.applied ?? {});
  const busy = outcomes.some((o) => o.status === "applying");
  const autoOff = !!card.googleAutoOff;
  // An approval was stopped because Google changed the profile on its own.
  const blocked = !autoOff && Array.isArray(card.googleUpdatedNow);
  // Said off on an earlier card a week or more ago: ask once, quietly.
  const recheckSince = settingsRecheckSince(card);
  const progress = liveProgress(card.applied);
  const lastChecked = outcomes.reduce<string | undefined>((a, o) => (o.checkedAt && (!a || o.checkedAt > a) ? o.checkedAt : a), undefined);

  // Live status: re-check changes still waiting on Google while the card is open.
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const checkRef = useRef(checkStatus);
  checkRef.current = checkStatus;
  const mounted = useRef(true);
  useEffect(() => () => void (mounted.current = false), []);
  const runCheck = useCallback(async (all = false) => {
    if (!checkRef.current) return;
    setChecking(true);
    const ok = await checkRef.current(all);
    if (!mounted.current) return;
    setCheckFailed(!ok);
    setChecking(false);
  }, []);
  const hasWaiting = outcomes.some((o) => isWaiting(o) && !!o.check);
  useEffect(() => {
    if (!checkStatus || !hasWaiting || card.loading) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      tries += 1;
      await runCheck();
      if (mounted.current && tries < STATUS_POLL_MAX) timer = setTimeout(tick, STATUS_POLL_MS);
    };
    timer = setTimeout(tick, STATUS_FIRST_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWaiting, card.loading, !!checkStatus, runCheck]);

  const allItems = useMemo(() => (plan?.status === "ok" ? approveAllItems(plan, card) : []), [plan, card]);

  const label = agentName ? `${agentName}: Business Profile proposal` : "Business Profile proposal";

  /* ── states before a plan ── */

  if (card.loading) return <GbpLoadingCard quiet={!!card.askedInChat} working />;

  if (card.planError || !plan) {
    return (
      <section aria-label={label} className={`${CARD} px-6 py-5`}>
        <p className="text-[14.5px] text-theme-fg">{card.planError || "I couldn't prepare the proposal just now."}</p>
        <p className={`mt-1 ${MUTED}`}>Ask me again in a moment. Nothing was changed on Google.</p>
      </section>
    );
  }

  if (plan.status !== "ok") {
    const reconnect = plan.status === "needs_reauth";
    const connect = reconnect || plan.status === "not_connected";
    return (
      <section aria-label={label} className={`${CARD} grid gap-3 px-6 py-5`}>
        <p className="text-[14.5px] text-theme-fg">{plan.detail || "I couldn't read your Google Business Profile just now."}</p>
        {connect && onConnect && (
          <div>
            <button type="button" onClick={onConnect} className={BTN_PRIMARY}>
              {reconnect ? "Reconnect Google" : "Connect Google Business Profile"}
            </button>
          </div>
        )}
      </section>
    );
  }

  /* ── one change at a time ── */

  const steps = [...rowsIn(plan, "fix"), ...rowsIn(plan, "team")];
  const firstOpen = Math.max(0, steps.findIndex((r) => !isClosed(r, card) && !card.skipped?.[r.id] && !card.doneOnGoogle?.[r.id]));
  const current = Math.min(step ?? firstOpen, Math.max(0, steps.length - 1));
  /** After the owner settles the change on screen, show the next one. */
  const moveOn = (row: GbpPlanRow) => {
    const i = steps.findIndex((r) => r.id === row.id);
    if (i === current && i < steps.length - 1) {
      setEditing(null);
      setStep(i + 1);
    }
  };

  /* ── actions ── */

  const answer = (row: GbpPlanRow, value: GbpEditValue) => {
    update({ ...card, planAnswers: { ...(card.planAnswers ?? {}), [row.id]: value } });
    setEditing(null);
  };
  const skip = (row: GbpPlanRow, on: boolean) => {
    update({ ...card, skipped: { ...(card.skipped ?? {}), [row.id]: on } });
    if (on) moveOn(row);
  };
  const markDone = (row: GbpPlanRow, on: boolean) => {
    update({ ...card, doneOnGoogle: { ...(card.doneOnGoogle ?? {}), [row.id]: on } });
    if (on) moveOn(row);
  };
  const approve = (row: GbpPlanRow) => {
    const item = applyItemFor(row, card);
    if (!item) return;
    onApply?.([item]);
    moveOn(row);
  };

  const openLibrary = () => {
    if (!loadMediaLibrary) return;
    const seq = ++librarySeq.current;
    setLibrary({ ...LIBRARY_CLOSED, open: true, loading: true });
    loadMediaLibrary(null)
      .then((page) => {
        if (seq === librarySeq.current) setLibrary({ ...LIBRARY_CLOSED, open: true, items: page.items, nextToken: page.nextToken });
      })
      .catch(() => {
        // Stay open and say so in the picker, with a retry; closing it here made
        // the picker flash and vanish.
        if (seq === librarySeq.current) setLibrary({ ...LIBRARY_CLOSED, open: true, failed: true });
      });
  };
  const loadMoreLibrary = () => {
    const token = library.nextToken;
    if (!loadMediaLibrary || !token || library.loadingMore) return;
    const seq = librarySeq.current;
    setLibrary((l) => ({ ...l, loadingMore: true, moreFailed: false }));
    loadMediaLibrary(token)
      .then((page) => {
        if (seq !== librarySeq.current) return;
        setLibrary((l) => {
          const seen = new Set(l.items.map((m) => m.media_id));
          return { ...l, loadingMore: false, items: [...l.items, ...page.items.filter((m) => !seen.has(m.media_id))], nextToken: page.nextToken };
        });
      })
      .catch(() => {
        if (seq === librarySeq.current) setLibrary((l) => ({ ...l, loadingMore: false, moreFailed: true }));
      });
  };
  const closeLibrary = () => {
    librarySeq.current++;
    setLibrary(LIBRARY_CLOSED);
  };
  const setPhoto = (next: { sourceUrl?: string; category?: GbpPhotoCategory | null }) =>
    update({
      ...card,
      photo: {
        sourceUrl: next.sourceUrl ?? card.photo?.sourceUrl ?? "",
        category: next.category !== undefined ? next.category : card.photo?.category ?? null,
      },
    });

  /* ── one row ── */

  const renderEditor = (row: GbpPlanRow) => {
    const value = rowValue(row, card);
    const cancel = () => setEditing(null);
    switch (row.field) {
      case "description":
        return (
          <TextValueEditor
            value={typeof value === "string" ? value : ""}
            multiline
            max={DESCRIPTION_MAX}
            label="Description"
            onSave={(v) => answer(row, v)}
            onCancel={cancel}
          />
        );
      case "services": {
        const list = ((Array.isArray(value) ? value : []) as Array<string | GbpServiceEdit>).map((x) =>
          typeof x === "string" ? { name: x, description: "" } : x,
        );
        return <ServiceLinesEditor value={list} onSave={(v) => answer(row, v)} onCancel={cancel} />;
      }
      case "phone":
      case "website":
        return (
          <TextValueEditor
            value={typeof value === "string" ? value : ""}
            max={row.field === "website" ? 2048 : 40}
            label={row.label}
            onSave={(v) => answer(row, v)}
            onCancel={cancel}
          />
        );
      case "regular_hours":
        return (
          <HoursEditor
            row={{ value: (value as FixValue) ?? null }}
            onSave={(v) => answer(row, v as GbpEditValue)}
            onCancel={cancel}
          />
        );
      case "opening_date":
        return (
          <DateEditor row={{ value: (value as FixValue) ?? null }} onSave={(v) => answer(row, v as GbpEditValue)} onCancel={cancel} />
        );
      case "primary_category":
        return (
          <CategoryEditor
            row={{ value: card.primaryAnswer ? [card.primaryAnswer] : [], spec: ASK_GAPS.no_additional_categories }}
            search={searchCategories}
            single
            onSave={(v) => {
              const picked = Array.isArray(v) ? (v as GbpCategoryOption[])[0] : null;
              update({ ...card, primaryAnswer: picked ?? null });
              setEditing(null);
            }}
            onCancel={cancel}
          />
        );
      default:
        return null;
    }
  };

  const canEdit = (row: GbpPlanRow) =>
    !!row.field && ["description", "services", "phone", "website", "regular_hours", "opening_date", "primary_category"].includes(row.field) &&
    !(row.field === "primary_category" && row.kind === "edit");

  const renderAttribute = (row: GbpPlanRow) => {
    const attr = row.meta?.attribute;
    if (!attr) return null;
    const chosen = card.planAnswers?.[row.id] as { attribute: string; value: boolean | string } | undefined;
    const pick = (v: boolean | string) => answer(row, { attribute: attr, value: v });
    if (row.meta?.value_type === "ENUM") {
      return (
        <select
          aria-label={row.label}
          value={chosen && typeof chosen.value === "string" ? chosen.value : ""}
          onChange={(e) => e.target.value && pick(e.target.value)}
          className={SELECT}
        >
          <option value="">Choose…</option>
          {(row.meta.options ?? []).map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.display_name || String(o.value)}</option>
          ))}
        </select>
      );
    }
    return (
      <div role="group" aria-label={row.label} className="flex gap-1.5">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            aria-pressed={chosen?.value === v}
            onClick={() => pick(v)}
            className={`h-8 rounded-full border px-3.5 text-[13px] font-medium ${
              chosen?.value === v ? "border-theme-primary bg-theme-primary text-white" : "border-theme-border text-theme-fg hover:border-theme-fg-muted"
            }`}
          >
            {v ? "Yes" : "No"}
          </button>
        ))}
      </div>
    );
  };

  const renderPhoto = (row: GbpPlanRow) => {
    const pick = card.photo;
    const settled = isSettled(outcomeOf(row, card));
    if (settled) return null;
    return (
      <div className="grid gap-2.5">
        {pick?.sourceUrl ? (
          <div className="flex flex-wrap items-center gap-3">
            <img src={pick.sourceUrl} alt="" className="h-16 w-16 rounded-[10px] border border-theme-border object-cover" />
            <select
              aria-label="What the photo shows"
              value={pick.category ?? ""}
              onChange={(e) => setPhoto({ category: (e.target.value || null) as GbpPhotoCategory | null })}
              className={SELECT}
            >
              <option value="">What does it show?</option>
              {GBP_PHOTO_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <button type="button" onClick={openLibrary} className={BTN_QUIET}>Choose another</button>
          </div>
        ) : (
          loadMediaLibrary && (
            <div>
              <button type="button" onClick={openLibrary} className={BTN_SECONDARY}>Choose from Media Library</button>
            </div>
          )
        )}
      </div>
    );
  };

  const renderHandOff = (row: GbpPlanRow) => {
    const spec = HANDOFF_GAPS[HANDOFF_SPEC[row.id] ?? "no_posts"];
    const member = spec.role === "receptionist" ? card.team?.receptionist : card.team?.socialMediaManager;
    return member && onHandOff ? (
      <button type="button" onClick={() => onHandOff(member.id, spec.prompt)} className={BTN_SECONDARY}>
        Ask {member.name}
      </button>
    ) : (
      <p className={MUTED}>Add a {spec.roleLabel} to your team and they can {spec.does}.</p>
    );
  };

  /** The row's buttons on the left, Back / Next (when shown one at a time) on the right. */
  const actionBar = (buttons: React.ReactNode, nav?: React.ReactNode) =>
    buttons || nav ? (
      <div className="flex flex-wrap items-center gap-2">
        {buttons}
        {nav && <div className="ml-auto">{nav}</div>}
      </div>
    ) : null;

  const renderActions = (row: GbpPlanRow, nav?: React.ReactNode) => {
    const outcome = outcomeOf(row, card);
    if (outcome && (isSettled(outcome) || outcome.status === "applying" || outcome.status === "not_on_google")) {
      return (
        <div className="grid gap-2">
          <div className="grid gap-1.5">
            <OutcomeLine outcome={outcome} />
            <WarningLine outcome={outcome} />
          </div>
          {actionBar(
            outcome.status === "not_on_google" && (
              <button type="button" disabled={busy || !autoOff} onClick={() => approve(row)} className={BTN_SECONDARY}>Try again</button>
            ),
            nav,
          )}
        </div>
      );
    }
    if (row.kind === "handoff") return actionBar(renderHandOff(row), nav);
    if (card.skipped?.[row.id] || card.doneOnGoogle?.[row.id]) {
      return actionBar(
        <button type="button" onClick={() => (card.skipped?.[row.id] ? skip(row, false) : markDone(row, false))} className={BTN_QUIET}>
          Undo
        </button>,
        nav,
      );
    }
    if (row.kind === "manual") {
      return (
        <div className="grid gap-2">
          {WHERE_ON_GOOGLE[row.id] && <p className={MUTED}>{WHERE_ON_GOOGLE[row.id]}</p>}
          {actionBar(
            <>
              <button type="button" onClick={() => markDone(row, true)} className={BTN_SECONDARY}>I've done this</button>
              <button type="button" onClick={() => skip(row, true)} className={BTN_QUIET}>Skip</button>
            </>,
            nav,
          )}
        </div>
      );
    }
    const item = applyItemFor(row, card);
    const retry = canRetry(outcome);
    // An answer row and the photo row show their send button only once there is
    // something to send; a prepared edit always shows it (disabled until ready).
    const showSend = row.kind === "edit" || !!item;
    return (
      <div className="grid gap-2">
        {outcome && <OutcomeLine outcome={outcome} />}
        {row.kind === "photo" && renderPhoto(row)}
        {row.field === "attributes" && renderAttribute(row)}
        {(row.kind === "ask" && !item && row.field !== "attributes" && editing !== row.id) && (
          <div>
            <button type="button" onClick={() => setEditing(row.id)} className={BTN_SECONDARY}>
              {row.field === "regular_hours" ? "Set your hours" : row.field === "primary_category" ? "Pick a category" : "Answer"}
            </button>
          </div>
        )}
        {actionBar(
          <>
            {showSend ? (
              <button
                type="button"
                disabled={!item || busy || !autoOff || !onApply}
                onClick={() => approve(row)}
                className={BTN_PRIMARY}
              >
                {retry ? "Try again" : row.kind === "photo" ? "Add photo" : row.kind === "ask" ? "Add to profile" : "Approve"}
              </button>
            ) : null}
            {canEdit(row) && editing !== row.id && (row.kind !== "ask" || !!item) && (
              <button type="button" disabled={busy} onClick={() => setEditing(row.id)} className={BTN_SECONDARY}>Edit</button>
            )}
            <button type="button" disabled={busy} onClick={() => skip(row, true)} className={BTN_QUIET}>Skip</button>
          </>,
          nav,
        )}
      </div>
    );
  };

  const renderRow = (row: GbpPlanRow, nav?: React.ReactNode) => {
    const [tone, word] = rowState(row, card);
    const closed = isClosed(row, card);
    return (
      <li key={row.id} id={`gbp-plan-${row.id.replace(/[^a-z0-9]+/gi, "-")}`} className="grid gap-2.5 px-5 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <h4 className={`text-[15px] font-semibold leading-snug ${closed ? "text-theme-fg-muted" : "text-theme-fg"}`}>{row.label}</h4>
          <span className="flex flex-shrink-0 items-center gap-2 text-[12.5px] text-theme-fg-muted">
            <Dot tone={tone} />
            {word}
            {card.revisedInChat?.[row.id] && <span>· changed in chat</span>}
          </span>
        </div>
        {!card.skipped?.[row.id] && (
          <>
            {editing === row.id ? renderEditor(row) : <Change row={row} card={card} />}
            {row.meta?.changes_primary && !closed && (
              <p className="text-[13px] leading-relaxed text-theme-fg">
                Changing your main category is a big change. Google may move services listed under the old one.
              </p>
            )}
            {row.meta?.confirm && !closed && (
              <p className="text-[13px] font-medium leading-relaxed text-theme-fg">{row.meta.confirm}</p>
            )}
            <p className="text-[14px] leading-relaxed text-theme-fg-muted">{row.reason}</p>
            {onAsk && (
              <div>
                <button
                  type="button"
                  onClick={() => onAsk(askAbout(row))}
                  className="text-[13px] font-medium text-theme-fg-muted underline-offset-2 hover:text-theme-fg hover:underline"
                >
                  Ask about this
                </button>
              </div>
            )}
            {editing === row.id ? actionBar(null, nav) : renderActions(row, nav)}
          </>
        )}
        {card.skipped?.[row.id] && renderActions(row, nav)}
      </li>
    );
  };

  /* ── the whole card ── */

  const fixes = rowsIn(plan, "fix");
  const team = rowsIn(plan, "team");
  const optional = rowsIn(plan, "optional");
  const problems = sourceProblems(plan);
  const open = openFixes(plan, card);
  // Rows that could be sent but wait for the owner's own look (Check first).
  const checkFirst = open.filter((r) => !inApproveAll(r, card) && applyItemFor(r, card)).length;
  const report = outcomeReport(card);
  const nothing = fixes.length === 0 && team.length === 0;

  return (
    <section aria-label={label} className={`${CARD} overflow-hidden`}>
      <header className="grid gap-2 px-5 pb-4 pt-5 sm:px-6">
        <p className={KICKER}>{plan.title ? `${plan.title} · Google Business Profile` : "Google Business Profile"}</p>
        <h3 className={TITLE}>
          {nothing
            ? "Your profile has everything the checks look for"
            : open.length === 0
              ? "Every change is handled"
              : `${plural(fixes.length, "change", "changes")} prepared for you`}
        </h3>
        {!card.askedInChat && plan.summary?.length ? (
          <p className="max-w-[62ch] text-[14px] leading-relaxed text-theme-fg-muted">{plan.summary.join(" ")}</p>
        ) : null}
        {!!plan.google_changed?.length && (
          <p role="note" className="text-[13.5px] leading-relaxed text-theme-fg">
            Google has changed your {namesList(plan.google_changed)} on its own, so its automatic updates are probably still
            on. Turn them off before you approve, or Google may undo your changes.
          </p>
        )}
        {problems.length > 0 && (
          <ul className="mt-1 grid gap-1" aria-label="What I couldn't use">
            {problems.map((p) => (
              <li key={p.name} className="flex items-baseline gap-2 text-[13px] leading-relaxed text-theme-fg-muted">
                <Dot tone={p.failed ? "failed" : "none"} />
                <span>
                  <span className="font-medium text-theme-fg">{p.name}:</span> {p.detail}
                </span>
              </li>
            ))}
          </ul>
        )}
      </header>

      {!nothing && !autoOff && recheckSince && (
        <div
          data-testid="gbp-settings-recheck"
          className="mx-5 mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] border border-theme-border px-4 py-3 sm:mx-6"
        >
          <p className="text-[13.5px] text-theme-fg">
            On {shortDate(recheckSince)} you told me Google's automatic updates were off. Still off?
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => update({ ...card, googleAutoOff: true })} className={BTN_SECONDARY}>
              Yes, still off
            </button>
            <button type="button" onClick={() => update({ ...card, googleBackOn: true })} className={BTN_QUIET}>
              They're back on
            </button>
          </div>
        </div>
      )}

      {!nothing && !autoOff && !recheckSince && (
        <div
          role={blocked ? "alert" : undefined}
          className="mx-5 mb-4 grid gap-2.5 rounded-[12px] border border-theme-border px-4 py-3.5 sm:mx-6"
        >
          <p className="text-[14px] font-semibold text-theme-fg">
            {blocked ? "Nothing was sent: Google is still changing your profile" : "First, turn off two Google settings"}
          </p>
          <p className={MUTED}>
            {blocked
              ? `Google changed ${card.googleUpdatedNow?.length ? `your ${namesList(card.googleUpdatedNow)}` : "your profile"} on its own just now. That means its automatic updates are still on, and it could undo what you approve here.`
              : "Google can change your profile on its own and undo what you approve here."}
          </p>
          <ol className="grid list-decimal gap-1 pl-5 text-[13.5px] leading-relaxed text-theme-fg">
            <li>Search Google for your business name, signed in to the account that manages it.</li>
            <li>Click ⋮ → Business Profile settings → Advanced settings.</li>
            <li>Switch off “Keep your profile up to date” and “Post to your profile on your behalf”.</li>
            {blocked && <li>If your profile shows updates from Google, open them and accept or reject each one.</li>}
          </ol>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => update({ ...card, googleAutoOff: true, googleUpdatedNow: undefined, googleBackOn: undefined })}
              className={BTN_SECONDARY}
            >
              I've turned them off
            </button>
            {onAsk && (
              <button
                type="button"
                onClick={() => onAsk(GBP_SETTINGS_QUESTION)}
                className={BTN_QUIET}
              >
                Not sure how? Ask me
              </button>
            )}
          </div>
        </div>
      )}

      {fixes.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 border-y border-theme-border bg-theme-background px-5 py-3 sm:px-6">
            <button
              type="button"
              disabled={!allItems.length || busy || !autoOff || !onApply}
              onClick={() => onApply?.(allItems)}
              className={BTN_PRIMARY}
            >
              {allItems.length ? `Approve all ${allItems.length}` : "Approve all"}
            </button>
            <span className="text-[13px] text-theme-fg-muted">
              {!autoOff
                ? "Turn off Google's automatic changes first."
                : allItems.length
                  ? `Each change goes to Google on its own. Skip any you don't want first.${
                      checkFirst ? " Changes marked “Check first” are approved one at a time." : ""
                    }`
                  : checkFirst
                    ? "Check each change marked “Check first”, then approve it on its own."
                  : open.length
                    ? "The rest need your answer or a change on Google."
                    : "Nothing left to approve."}
            </span>
          </div>
        </>
      )}

      {steps.length > 0 && (
        <div data-testid="gbp-step" className={fixes.length > 0 ? "" : "border-t border-theme-border"}>
          <div className="flex items-center justify-between gap-3 px-5 pt-4 sm:px-6">
            <p className={KICKER}>
              {team.some((r) => r.id === steps[current].id) ? "For your team · " : ""}
              {current + 1} of {steps.length}
            </p>
            <span aria-hidden="true" className="hidden items-center gap-1 sm:flex">
              {steps.map((r, i) => (
                <span
                  key={r.id}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current
                      ? "w-4 bg-theme-fg"
                      : isClosed(r, card) || card.skipped?.[r.id] || card.doneOnGoogle?.[r.id]
                        ? "w-1.5 bg-brand-primary"
                        : "w-1.5 bg-theme-border"
                  }`}
                />
              ))}
            </span>
          </div>
          <ol key={steps[current].id} className="motion-safe:animate-statusIn">
            {renderRow(
              steps[current],
              <nav aria-label="Changes" className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={current === 0}
                  onClick={() => { setEditing(null); setStep(current - 1); }}
                  className={BTN_QUIET}
                  aria-label="Previous change"
                >
                  ‹ Back
                </button>
                <button
                  type="button"
                  disabled={current >= steps.length - 1}
                  onClick={() => { setEditing(null); setStep(current + 1); }}
                  className={BTN_QUIET}
                  aria-label="Next change"
                >
                  Next ›
                </button>
              </nav>,
            )}
          </ol>
        </div>
      )}

      {(optional.length > 0 || !!plan.hidden_walk_in) && (
        <details className="group border-t border-theme-border">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-3.5 text-[14px] font-semibold text-theme-fg sm:px-6 [&::-webkit-details-marker]:hidden">
            <span>
              Optional details{optional.length ? ` (${optional.length})` : ""}
              <span className="ml-2 text-[13px] font-normal text-theme-fg-muted">Answer only what applies. Skipping is fine.</span>
            </span>
            <span aria-hidden="true" className="text-theme-fg-muted transition-transform group-open:rotate-90">›</span>
          </summary>
          {!!plan.hidden_walk_in && (
            <p className={`px-5 pb-2 sm:px-6 ${MUTED}`}>
              {plural(plan.hidden_walk_in, "question", "questions")} for walk-in customers (parking, restrooms, access) hidden: your
              profile has no storefront address.
            </p>
          )}
          <ul className="divide-y divide-theme-border border-t border-theme-border">{optional.map((r) => renderRow(r))}</ul>
        </details>
      )}

      {report.length > 0 && (
        <div role="status" className="grid gap-1 border-t border-theme-border px-5 py-3.5 sm:px-6">
          <p className={KICKER}>What happened on Google</p>
          {report.map((line) => (
            <p key={line} className="text-[13.5px] leading-relaxed text-theme-fg">{line}</p>
          ))}
        </div>
      )}

      {progress.total > 0 && (
        <footer className="flex flex-wrap items-center gap-3 border-t border-theme-border px-5 py-3.5 sm:px-6">
          <span className="text-[13.5px] text-theme-fg">
            {progress.live} of {plural(progress.total, "change", "changes")} live on Google
            {progress.waiting > 0 ? ` · ${progress.waiting} waiting for Google` : ""}
          </span>
          <span className="flex-1" />
          {checkStatus && (
            <button type="button" disabled={checking} onClick={() => void runCheck(true)} className={BTN_QUIET}>
              {checking ? "Checking…" : "Check again"}
            </button>
          )}
          {checkFailed ? (
            <span className="text-[12.5px] text-theme-fg-muted">Couldn't reach Google just now.</span>
          ) : (
            lastChecked && <span className="text-[12.5px] text-theme-fg-muted">Checked {new Date(lastChecked).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
          )}
        </footer>
      )}

      {library.open &&
        createPortal(
          <div onClick={stopPropagation} onKeyDown={stopPropagation}>
            <MediaLibraryModal
              isOpen
              onClose={closeLibrary}
              mediaItems={library.items}
              selectedImages={[]}
              onImageSelect={(item) => {
                const url = getMediaThumbnailUrl(item);
                if (url) setPhoto({ sourceUrl: url });
                closeLibrary();
              }}
              loading={library.loading}
              mediaTypeFilter="image"
              subtitle="Choose one photo for your Google Business Profile."
              emptyMessage={
                library.failed ? (
                  <div role="alert" className="grid justify-items-center gap-3">
                    <p>Your Media Library couldn't be opened just now.</p>
                    <button type="button" onClick={openLibrary} className={BTN_SECONDARY}>Try again</button>
                  </div>
                ) : (
                <p>
                  {library.nextToken
                    ? "No photos you can use in this part of your library. Load more to keep looking."
                    : "No photos you can use yet. Add one in your Media Library, then come back here."}
                </p>
                )
              }
              onLoadMore={loadMoreLibrary}
              hasMore={!!library.nextToken}
              loadingMore={library.loadingMore}
              loadMoreFailed={library.moreFailed}
            />
          </div>,
          document.body,
        )}
    </section>
  );
};
